import Axios, { AxiosError } from "axios";
import {
  CacheInt,
  RequestInt,
  RequestParamsInt,
  ResponseErrorInt,
} from "./type";
import { GENERIC_BUSINESS_WORDS } from "./constant";

const BASE_URL = import.meta.env.VITE_BASE_URL;

export const devLog = (...args: unknown[]) => {
  const [title, ...rest] = args;
  if (import.meta.env.VITE_ENV !== "prod")
    console.log(`\n\n\n\n=======${title}=======\n`, rest);
};

const parseJSON = (value: string) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error("Error parsing JSON", error);
    return value;
  }
};

const validateCacheKey = (key: string) => {
  if (!key || typeof key !== "string")
    throw new Error("Invalid key provided to cache");
};

export const cache: CacheInt = {
  get: (key) => {
    validateCacheKey(key);

    const cachedData = localStorage.getItem(key);
    return cachedData ? parseJSON(cachedData) : null;
  },
  set: (key, value): void => {
    validateCacheKey(key);

    localStorage.setItem(key, JSON.stringify(value));
  },
  del: (key) => {
    validateCacheKey(key);
    localStorage.removeItem(key);
  },
};

const GENERIC_SERVER_ERROR = "Something went wrong. Please try again shortly.";

export const handleError = (error: Error | ResponseErrorInt): void => {
  devLog("Error", error);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const customError = error as any;
  const httpStatusCode = customError?.httpStatusCode;

    const isServerError = !httpStatusCode || httpStatusCode >= 500;

   const errorMessage = isServerError
    ? GENERIC_SERVER_ERROR
    : (customError.message as string) || "An unknown error occurred";

  const errorType = isServerError
    ? "SERVER_ERROR"
    : (customError.error as string) || "An unknown error occurred";

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errorResponse: any = {
    status: false,
    message: errorMessage || "An unknown error occurred",
    error: errorType || "An unknown error occurred",
  };

  if (customError?.httpStatusCode)
    errorResponse.httpStatusCode = customError.httpStatusCode;

  if (customError?.errorCode) errorResponse.errorCode = customError.errorCode;

  throw errorResponse;
};

const httpClient = async (params: RequestParamsInt) => {
  try {
    const { data, headers, isExternalUrl, auth = true, ...requestParams } = params;

    if (isExternalUrl && !requestParams.url.startsWith("http"))
      throw new Error("Invalid URL provided");

    const url = isExternalUrl
      ? requestParams.url
      : `${BASE_URL}${requestParams.url}`;

    const authToken = cache.get("TOKEN") as string;

    const requestHeaders = { 
      ...headers, 
      // Authorization: `Bearer ${authToken}` 
      ...(auth && authToken ? { Authorization: `Bearer ${authToken}` } : {}),

    };   

    const response = await Axios({
      ...requestParams,
      headers: requestHeaders,
      data,
      url,
    });

    devLog("Response", response.data);

    return response.data;
  } catch (error) {
    const axiosError = error as unknown as AxiosError;
    handleError((axiosError?.response?.data as ResponseErrorInt) || axiosError);
  }
};

export const request: RequestInt = {
  post: async (params) => httpClient({ ...params, method: "POST" }),
  get: async (params) => httpClient({ ...params, method: "GET" }),
  patch: async (params) => httpClient({ ...params, method: "PATCH" }),
  delete: async (params) => httpClient({ ...params, method: "DELETE" }),
};

export const truncateAddress = (address?: string, start = 10, end = 4) => {
  if (!address) return "";
  return `${address.slice(0, start)}...${address.slice(-end)}`;``
};

export const validateBusinessAccountName = (
  inputName: string,
  storedName: string,
  minMatches: number = 1 
): boolean => {
  if (!inputName || !storedName) return true;

  const businessNameParts = storedName
    .toLowerCase()
    .split(/[\s\/,.-]+/)
    .filter(Boolean)
    .filter((part) => !GENERIC_BUSINESS_WORDS.includes(part));

  const normalizedInput = inputName.toLowerCase();

  const matches = businessNameParts.filter((part) =>
    normalizedInput.includes(part)
  ).length;

  return matches >= minMatches;
};
